import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosInstance from './axiosInstance';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Add, DeleteOutline, EditOutlined } from '@mui/icons-material';
import './ExportGridView.css';

const buildBudgetPeriods = ({ pastMonths = 12, futureMonths = 12 } = {}) => {
  const periods = [];
  const currentDate = new Date();
  for (let offset = -pastMonths; offset <= futureMonths; offset += 1) {
    const periodDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1);
    periods.push({
      label: periodDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
      month: periodDate.getMonth() + 1,
      year: periodDate.getFullYear(),
      value: `${periodDate.getFullYear()}-${String(periodDate.getMonth() + 1).padStart(2, '0')}`,
    });
  }
  return periods;
};

const monthOptions = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 7 }, (_, index) => currentYear - 2 + index);

const defaultFormState = {
  budget_month: new Date().getMonth() + 1,
  budget_year: currentYear,
  site_name: '',
  location_id: '',
  consumables: '',
  paint: '',
  fleet: '',
  car_hire: '',
};

const managePaintColumnWidths = {
  site_name: '28%',
  consumables: '15%',
  paint: '15%',
  fleet: '15%',
  car_hire: '15%',
  actions: '12%',
};

const ALL_SITES_VALUE = '__all_sites__';

const ManagePaintConsumablesPage = () => {
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const budgetPeriodOptions = useMemo(() => buildBudgetPeriods(), []);
  const defaultBudgetPeriod = budgetPeriodOptions.find((period) => {
    const today = new Date();
    return period.month === today.getMonth() + 1 && period.year === today.getFullYear();
  }) || budgetPeriodOptions[budgetPeriodOptions.length - 1] || null;
  const allSitesOption = useMemo(() => ({ location_id: ALL_SITES_VALUE, name: 'All Locations' }), []);
  const [selectedPeriod, setSelectedPeriod] = useState(defaultBudgetPeriod);
  const [selectedLocation, setSelectedLocation] = useState(allSitesOption);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [pageInput, setPageInput] = useState('1');
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortKey, setSortKey] = useState('budget_period_label');
  const [sortDirection, setSortDirection] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [originalRecordKey, setOriginalRecordKey] = useState(null);
  const [formData, setFormData] = useState(defaultFormState);
  const [formError, setFormError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingRecordKey, setDeletingRecordKey] = useState('');

  const showToast = useCallback((message, severity = 'success') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('hv-toast', { detail: { message, severity } }));
  }, []);

  const sortedLocations = useMemo(
    () => [...locations].sort((left, right) => {
      const leftName = (left?.name || left?.location_id || '').toString().toLowerCase();
      const rightName = (right?.name || right?.location_id || '').toString().toLowerCase();
      return leftName.localeCompare(rightName);
    }),
    [locations]
  );

  const loadLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const response = await axiosInstance.get('/api/v1/location');
      const payload = response?.data?.data ?? response?.data ?? [];
      setLocations(Array.isArray(payload) ? payload : []);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load locations.', 'error');
    } finally {
      setLoadingLocations(false);
    }
  }, [showToast]);

  const loadRecords = useCallback(async (pageIndex = page, nextSortKey = sortKey, nextSortDirection = sortDirection) => {
    setLoadingRecords(true);
    try {
      const response = await axiosInstance.get('/api/v1/manage/paint-consumables', {
        params: {
          page: pageIndex + 1,
          limit: rowsPerPage,
          sort_key: nextSortKey,
          sort_direction: nextSortDirection,
          ...(selectedPeriod ? { budget_month: selectedPeriod.month, budget_year: selectedPeriod.year } : {}),
          ...(selectedLocation?.location_id && selectedLocation.location_id !== ALL_SITES_VALUE ? { location_id: selectedLocation.location_id } : {}),
        },
      });
      const payload = response?.data ?? {};
      const nextRecords = Array.isArray(payload?.data) ? payload.data : [];
      setRecords(nextRecords);
      setTotalRecords(Number(payload?.total ?? nextRecords.length ?? 0));
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to load paint & consumables records.', 'error');
    } finally {
      setLoadingRecords(false);
    }
  }, [page, rowsPerPage, selectedLocation, selectedPeriod, showToast, sortDirection, sortKey]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadRecords(page, sortKey, sortDirection);
  }, [loadRecords, page, sortKey, sortDirection]);

  useEffect(() => {
    setPageInput(String(page + 1));
  }, [page, totalRecords]);

  useEffect(() => {
    setPage(0);
  }, [selectedPeriod, selectedLocation]);

  const closeDialog = () => {
    if (savingRecord) return;
    setDialogOpen(false);
    setFormError('');
    setFormData(defaultFormState);
    setFormMode('create');
    setOriginalRecordKey(null);
  };

  const openCreateDialog = () => {
    setFormMode('create');
    setOriginalRecordKey(null);
    setFormData(defaultFormState);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (record) => {
    setFormMode('edit');
    setOriginalRecordKey({
      budget_year: record?.budget_year,
      budget_month: record?.budget_month,
      site_key: record?.site_key,
    });
    setFormData({
      budget_month: Number(record?.budget_month) || new Date().getMonth() + 1,
      budget_year: Number(record?.budget_year) || currentYear,
      site_name: record?.site_name || '',
      location_id: record?.location_id || '',
      consumables: record?.consumables ?? '',
      paint: record?.paint ?? '',
      fleet: record?.fleet ?? '',
      car_hire: record?.car_hire ?? '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const getRecordKey = (record) => `${record?.budget_year || ''}-${record?.budget_month || ''}-${record?.site_key || ''}`;

  const openDeleteDialog = (record) => {
    setDeleteTarget(record);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingRecordKey) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.budget_month) return 'Budget month is required.';
    if (!formData.budget_year) return 'Budget year is required.';
    if (!formData.location_id) return 'Location is required.';
    return '';
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setSavingRecord(true);
    setFormError('');
    try {
      const selectedLocation = sortedLocations.find((location) => location.location_id === formData.location_id);
      const payload = {
        budget_month: Number(formData.budget_month),
        budget_year: Number(formData.budget_year),
        location_id: selectedLocation?.location_id || '',
        consumables: formData.consumables,
        paint: formData.paint,
        fleet: formData.fleet,
        car_hire: formData.car_hire,
      };

      if (formMode === 'edit' && originalRecordKey) {
        await axiosInstance.put(
          `/api/v1/manage/paint-consumables/${encodeURIComponent(originalRecordKey.budget_year)}/${encodeURIComponent(originalRecordKey.budget_month)}/${encodeURIComponent(originalRecordKey.site_key || '')}`,
          payload
        );
      } else {
        await axiosInstance.post('/api/v1/manage/paint-consumables', payload);
      }

      showToast(formMode === 'edit' ? 'Paint & consumables record updated successfully.' : 'Paint & consumables record created successfully.', 'success');
      closeDialog();
      await loadRecords(page, sortKey, sortDirection);
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || 'Failed to save paint & consumables record.';
      setFormError(message);
      showToast(message, 'error');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    const recordKey = getRecordKey(deleteTarget);
    setDeletingRecordKey(recordKey);
    try {
      await axiosInstance.delete(
        `/api/v1/manage/paint-consumables/${encodeURIComponent(deleteTarget?.budget_year)}/${encodeURIComponent(deleteTarget?.budget_month)}/${encodeURIComponent(deleteTarget?.site_key || '')}`
      );
      showToast('Paint & consumables record deleted successfully.', 'success');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await loadRecords(page, sortKey, sortDirection);
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Failed to delete paint & consumables record.', 'error');
    } finally {
      setDeletingRecordKey('');
    }
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage === page) return;
    setPage(newPage);
  };

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!totalRecords) {
      setPageInput('1');
      return;
    }
    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(page + 1));
      return;
    }
    const totalPages = Math.max(Math.ceil(totalRecords / rowsPerPage), 1);
    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages);
    if (clampedPage !== page + 1) {
      setPage(clampedPage - 1);
    } else {
      setPageInput(String(clampedPage));
    }
  };

  const handleSortChange = (columnKey) => {
    setSortKey((current) => {
      if (current === columnKey) {
        setSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortDirection('asc');
      }
      return columnKey;
    });
    setPage(0);
  };

  const renderSortIcon = (columnKey) => {
    if (sortKey !== columnKey) return null;
    return sortDirection === 'asc' ? <ArrowUpward className="sortIcon" /> : <ArrowDownward className="sortIcon" />;
  };

  const handleFormLocationChange = (locationId) => {
    const selectedLocation = sortedLocations.find((location) => location.location_id === locationId);
    setFormData((current) => ({
      ...current,
      location_id: locationId,
      site_name: selectedLocation?.name || '',
    }));
  };

  const hasActiveFilters = Boolean(
    (selectedPeriod?.value || '') !== (defaultBudgetPeriod?.value || '')
    || (selectedLocation?.location_id || ALL_SITES_VALUE) !== ALL_SITES_VALUE
  );

  const clearFilters = () => {
    setSelectedPeriod(defaultBudgetPeriod);
    setSelectedLocation(allSitesOption);
    setPage(0);
  };

  const formatCurrency = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '-';
    return `£${numericValue.toFixed(2)}`;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', textAlign: 'left' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ mb: 0.5, fontWeight: 500 }}>
              Paint &amp; Consumables
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="period-filter-label">Period</InputLabel>
            <Select
              labelId="period-filter-label"
              value={selectedPeriod?.value || ''}
              label="Period"
                onChange={(event) => {
                  const nextPeriod = budgetPeriodOptions.find((period) => period.value === event.target.value) || null;
                  setSelectedPeriod(nextPeriod);
                }}
              >
                {budgetPeriodOptions.map((period) => (
                  <MenuItem key={period.value} value={period.value}>
                    {period.label}
                  </MenuItem>
                ))}
              </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="site-filter-label">Location</InputLabel>
            <Select
              labelId="site-filter-label"
              value={selectedLocation?.location_id || ALL_SITES_VALUE}
              label="Location"
              onChange={(event) => {
                const nextLocation = [allSitesOption, ...sortedLocations].find((location) => location.location_id === event.target.value) || allSitesOption;
                setSelectedLocation(nextLocation);
              }}
              disabled={loadingLocations}
            >
              {[allSitesOption, ...sortedLocations].map((location) => (
                <MenuItem key={location.location_id || 'all-sites'} value={location.location_id || ALL_SITES_VALUE}>
                  {location.name || location.location_id || 'All Locations'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              sx={{ minWidth: 130, mr: 2, whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog} sx={{ minWidth: 210 }}>
              Add Record
            </Button>
          </Box>
          </Stack>
        </Box>

        <TableContainer
          sx={{
            position: 'relative',
            maxHeight: '68vh',
            minHeight: loadingRecords && !records.length ? 200 : 'auto',
            mt: 2,
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          {loadingRecords && records.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                backdropFilter: 'blur(1px)',
                pointerEvents: 'none',
                zIndex: 3,
              }}
            >
              <CircularProgress size={48} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                Loading next page...
              </Typography>
            </Box>
          )}
          <Table
            stickyHeader
            size="small"
            sx={{
              '& .MuiTableCell-sizeSmall': {
                padding: '8px 16px',
                height: 44,
                boxSizing: 'border-box',
              },
              '& .MuiTableHead .MuiTableCell-sizeSmall': {
                padding: '12px 16px',
                height: 48,
              },
              '& .MuiInputBase-root.MuiInputBase-sizeSmall': {
                minHeight: 28,
              },
              '& .MuiSelect-select.MuiSelect-select': {
                paddingTop: '4px',
                paddingBottom: '4px',
              },
              '& .MuiButton-sizeSmall': {
                minHeight: 28,
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell className={`sortableHeader ${sortKey === 'site_name' ? 'sorted' : ''}`} sx={{ width: managePaintColumnWidths.site_name, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('site_name')} role="button" tabIndex={0}>
                    <strong>Location</strong>
                    {renderSortIcon('site_name')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'consumables' ? 'sorted' : ''}`} sx={{ width: managePaintColumnWidths.consumables, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('consumables')} role="button" tabIndex={0}>
                    <strong>Consumables</strong>
                    {renderSortIcon('consumables')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'paint' ? 'sorted' : ''}`} sx={{ width: managePaintColumnWidths.paint, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('paint')} role="button" tabIndex={0}>
                    <strong>Paint</strong>
                    {renderSortIcon('paint')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'fleet' ? 'sorted' : ''}`} sx={{ width: managePaintColumnWidths.fleet, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('fleet')} role="button" tabIndex={0}>
                    <strong>Fleet</strong>
                    {renderSortIcon('fleet')}
                  </div>
                </TableCell>
                <TableCell className={`sortableHeader ${sortKey === 'car_hire' ? 'sorted' : ''}`} sx={{ width: managePaintColumnWidths.car_hire, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent" onClick={() => handleSortChange('car_hire')} role="button" tabIndex={0}>
                    <strong>Car Hire</strong>
                    {renderSortIcon('car_hire')}
                  </div>
                </TableCell>
                <TableCell className="sortableHeader" sx={{ width: managePaintColumnWidths.actions, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <div className="headerContent">
                    <strong>Actions</strong>
                  </div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingRecords && !records.length ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <CircularProgress size={18} thickness={4} />
                      <Typography color="text.secondary">Loading...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : records.length > 0 ? (
                records.map((record) => (
                  <TableRow key={`${record?.budget_year}-${record?.budget_month}-${record?.site_key}`} hover>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record?.location_name || record?.site_name || '-'}</TableCell>
                    <TableCell>{formatCurrency(record?.consumables)}</TableCell>
                    <TableCell>{formatCurrency(record?.paint)}</TableCell>
                    <TableCell>{formatCurrency(record?.fleet)}</TableCell>
                    <TableCell>{formatCurrency(record?.car_hire)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(record)} aria-label="edit paint and consumables record">
                          <EditOutlined fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(record)} aria-label="delete paint and consumables record">
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    No paint &amp; consumables records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputKeyDown}
              label="Page"
              size="small"
              sx={{ width: 90 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              disabled={loadingRecords || !totalRecords}
            />
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `of ${Math.max(Math.ceil(totalRecords / rowsPerPage), 1)}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalRecords ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRecords)} of ${totalRecords}` : 'No records to show'}
            </Typography>
          </Box>
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[rowsPerPage]}
            showFirstButton
            showLastButton
            labelRowsPerPage=""
            labelDisplayedRows={() => ''}
            sx={{
              '.MuiTablePagination-toolbar': { p: 0 },
              '.MuiTablePagination-selectLabel': { display: 'none' },
              '.MuiTablePagination-displayedRows': { m: 0 },
            }}
          />
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{formMode === 'edit' ? 'Edit Paint & Consumables Record' : 'Add Paint & Consumables Record'}</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="budget-month-label">Month</InputLabel>
                <Select
                  labelId="budget-month-label"
                  value={formData.budget_month}
                  label="Month"
                  onChange={(event) => handleFormChange('budget_month', event.target.value)}
                >
                  {monthOptions.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="budget-year-label">Year</InputLabel>
                <Select
                  labelId="budget-year-label"
                  value={formData.budget_year}
                  label="Year"
                  onChange={(event) => handleFormChange('budget_year', event.target.value)}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <FormControl fullWidth size="small">
              <InputLabel id="location-label">Location</InputLabel>
              <Select
                labelId="location-label"
                value={formData.location_id}
                label="Location"
                onChange={(event) => handleFormLocationChange(event.target.value)}
                disabled={loadingLocations}
              >
                <MenuItem value="" disabled>
                  <em>Select Location</em>
                </MenuItem>
                {formData.location_id && !sortedLocations.some((location) => location.location_id === formData.location_id) && (
                  <MenuItem value={formData.location_id} disabled>
                    {formData.site_name || formData.location_id}
                  </MenuItem>
                )}
                {sortedLocations.map((location) => (
                  <MenuItem key={location.location_id} value={location.location_id}>
                    {location.name || location.location_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Consumables"
              value={formData.consumables}
              onChange={(event) => handleFormChange('consumables', event.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Paint"
              value={formData.paint}
              onChange={(event) => handleFormChange('paint', event.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Fleet"
              value={formData.fleet}
              onChange={(event) => handleFormChange('fleet', event.target.value)}
              fullWidth
              type="number"
            />
            <TextField
              label="Car Hire"
              value={formData.car_hire}
              onChange={(event) => handleFormChange('car_hire', event.target.value)}
              fullWidth
              type="number"
            />
            {formError && (
              <Typography variant="body2" color="error">
                {formError}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={savingRecord}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={savingRecord}>
            {savingRecord ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Paint &amp; Consumables Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteTarget?.location_name || deleteTarget?.site_name || '-'} for {deleteTarget?.budget_period_label || '-'}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingRecordKey)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={Boolean(deletingRecordKey)}>
            {deletingRecordKey ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagePaintConsumablesPage;
